import { ChevronLeft } from "lucide-react";
import { LogoLockup, LogoMark } from "@/shared/components/brand/logo-mark";
import { SidebarSearchTrigger } from "@/shared/components/command-menu/sidebar-search-trigger";
import { SidebarFooter } from "@/shared/components/layout/sidebar-footer";
import { SidebarNav } from "@/shared/components/layout/sidebar-nav";
import { WorkspaceSwitcher } from "@/shared/components/layout/sidebar-workspace";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

/**
 * Application sidebar. Sticky 5-row grid:
 *   1. product brand       (auto)
 *   2. workspace switcher  (auto)
 *   3. command menu search (auto)
 *   4. nav                 (1fr, scrolls)
 *   5. footer              (auto)
 *
 * Collapse: ⌘\ or click the toggle on the right edge. Width animates via the
 * parent AppShell's grid-template-columns. Mobile: hidden on viewports < md.
 */
export function Sidebar() {
  return (
    <aside
      aria-label="Application"
      className={cn(
        "sticky top-0 z-10 hidden h-svh w-full shrink-0",
        "grid-rows-[auto_auto_auto_1fr_auto] grid-cols-1",
        "border-r border-border bg-surface-subtle",
        "md:grid",
      )}
    >
      <SidebarBrand />
      <WorkspaceSwitcher />
      <SidebarSearchTrigger />
      <SidebarNav />
      <SidebarFooter />
      <CollapseToggle />
    </aside>
  );
}

function SidebarBrand() {
  const { collapsed } = useSidebarCollapsed();

  return (
    <div className={cn("border-b border-border", collapsed ? "px-2.5 py-3" : "px-3 py-3.5")}>
      {collapsed ? (
        <div className="flex justify-center">
          <LogoMark className="size-8" />
        </div>
      ) : (
        <LogoLockup className="w-fit" showBeta={false} />
      )}
    </div>
  );
}

/**
 * Toggle button half-poking out of the sidebar's right edge. Always present;
 * the chevron flips when collapsed (left → collapse, right → expand).
 */
function CollapseToggle() {
  const { collapsed, toggle } = useSidebarCollapsed();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      title={collapsed ? "Expand (⌘\\)" : "Collapse (⌘\\)"}
      className={cn(
        "absolute bottom-3 -right-3.5 z-20 grid size-7 place-items-center rounded-full",
        "border border-border bg-card text-text-tertiary shadow-sm",
        "outline-none transition-all duration-150",
        "hover:text-foreground hover:shadow-md",
        "focus-visible:shadow-ring-focus",
      )}
    >
      <ChevronLeft
        className={cn("size-3.5 transition-transform duration-200", collapsed && "rotate-180")}
      />
    </button>
  );
}
